/**
 * Project 2026-005 — code-defined highlight tours for checklist steps 4–7 (§7.9, D1).
 * `enterRoute` is applied when a screen becomes active; `targetSelector` uses `[data-help="…"]`.
 */

export const CAMPAIGN_NEW_GENERAL_SEARCH = '?type=generalcampaign';

export const ONBOARDING_TOURS = {
  4: {
    completionInputKey: 'guestListComplete',
    screens: [
      {
        id: 'contacts-nav',
        enterRoute: { pathname: '/customers', search: '' },
        targetSelector: '[data-help="side-nav-contacts"]',
        title: "Let's Build Your Guest List",
        body:
          'Head to the "Guest List" tab to start adding your guests.',
        primaryLabel: 'Next',
        placement: 'right',
        pointerPosition: 'center',
      },
      {
        id: 'guest-welcome',
        enterRoute: { pathname: '/customers', search: '' },
        centered: true,
        title: 'Welcome to Your Guest List',
        body:
          'Here you can add new guests, manage existing ones, and keep your contacts organized.',
        primaryLabel: 'Next',
      },
      {
        id: 'add-contacts',
        enterRoute: { pathname: '/customers', search: '' },
        targetSelector: '[data-help="contacts-add-contacts"]',
        title: 'Add Your Guest List',
        body:
          'You\'ll upload a .CSV file or add guests manually - whatever works best for you. When you upload a .CSV file, remember to select the column in your file for cellphone numbers under Map Columns. We\'ll de-duplicate numbers, adjust number formats, etc. for you.',
        primaryLabel: 'Add Your Contacts Now',
        isFinal: true,
        placement: 'bottom',
        pointerPosition: 'center',
      },
    ],
  },
  5: {
    completionInputKey: 'widgetComplete',
    screens: [
      {
        id: 'integration-tab',
        enterRoute: { pathname: '/settings', search: '?settingsTab=integration' },
        targetSelector: '[data-help="side-nav-settings"]',
        title: 'Set Up Your Widget',
        body:
          'Head to the "Integration" tab on the Settings screen to get started with your website widget.',
        primaryLabel: 'Next',
        placement: 'right',
        pointerPosition: 'center',
      },
      {
        id: 'widget-welcome',
        enterRoute: { pathname: '/settings', search: '?settingsTab=integration' },
        centered: true,
        title: 'Welcome to Your Widget',
        body:
          'This is a pop-up or button that lives on your website so guests can sign up for your texts. Guests just enter their number to opt-in and subscribe to your text messages. Easy!',
        primaryLabel: 'Next',
      },
      {
        id: 'widget-style',
        enterRoute: { pathname: '/settings', search: '?settingsTab=integration' },
        targetSelector: '[data-help="integration-widget-style"]',
        scrollIntoView: true,
        title: 'Choose Your Widget Style',
        body:
          'Your widget can display on your website either as an auto pop-up or button. You can customize the text on the button or pop-up, create your own "thank you" message to guests when they enroll, and even change the widget\'s colors to match your brand\'s.',
        primaryLabel: 'Next',
        placement: 'right',
        pointerPosition: 'center',
      },
      {
        id: 'widget-embed',
        enterRoute: { pathname: '/settings', search: '?settingsTab=integration' },
        targetSelector: '[data-help="integration-embed-head-script"]',
        scrollIntoView: true,
        title: 'Place Widget Code on Your Site',
        body:
          'Once you\'re done customizing, click Update Configurations, then just copy and paste your code into the <head> of your site or anywhere you want the button to show up. If you need a hand doing this, shoot us a message at customerservice@checkthisout.io - we\'ll walk you through it.',
        primaryLabel: 'Create Widget',
        isFinal: true,
        placement: 'right',
        pointerPosition: 'center',
      },
    ],
  },
  6: {
    completionInputKey: 'qrCodeComplete',
    screens: [
      {
        id: 'self-opt-in-tab',
        enterRoute: { pathname: '/settings', search: '?settingsTab=selfOptIn' },
        targetSelector: '[data-help="settings-self-opt-in-tab"]',
        scrollWindowToTop: true,
        title: 'QR Codes',
        body:
          'Click "Self Opt-In" to generate a QR code that guests can scan to sign up for your texts.',
        primaryLabel: 'Next',
        placement: 'bottom',
        pointerPosition: 'center',
      },
      {
        id: 'qr-welcome',
        enterRoute: { pathname: '/settings', search: '?settingsTab=selfOptIn' },
        centered: true,
        title: 'Meet Your QR Code',
        body:
          'Your custom QR code lets guests easily opt in for text messages with a simple code scan. They can also text a keyword of your choosing to your number to opt in. You can place this code or keyword in-store, on menus, or anywhere guests are for quick opt-ins.',
        primaryLabel: 'Next',
      },
      {
        id: 'qr-customize',
        enterRoute: { pathname: '/settings', search: '?settingsTab=selfOptIn' },
        targetSelector: '[data-help="self-opt-in-customize"]',
        title: 'Customize & Share Your QR Code',
        body:
          'Please click on the Information icons next to fields on this page and follow examples for each field to comply with opt-in regulations. You can then place this QR code or keyword in-store, on menus, or anywhere guests are for quick opt-ins. Once you\'ve customized your keyword and messaging, simply click "Update Self Opt-In" then click "Generate Template". Your QR code template will automatically download for you to print and share.',
        primaryLabel: 'Customize',
        isFinal: true,
        placement: 'right',
        pointerPosition: 'center',
        scrollIntoView: true,
      },
    ],
  },
  7: {
    completionInputKey: 'campaignComplete',
    screens: [
      {
        id: 'campaigns-list',
        enterRoute: { pathname: '/campaigns', search: '' },
        targetSelector: '[data-help="side-nav-sms-campaigns"]',
        title: "Let's Start a SMS Campaign",
        body:
          'Head to the "Campaigns" tab to create and save your first campaign.\n\nNote: You will not be able to send your first campaign until your account is verified by the cell carriers. This process takes 2-7 days. Check This Out will file the verification paperwork for you and let you know when your account is verified.',
        primaryLabel: 'Next',
        placement: 'right',
        pointerPosition: 'center',
      },
      {
        id: 'campaign-create-new',
        enterRoute: { pathname: '/campaigns', search: '' },
        targetSelector: '[data-help="campaigns-create-new"]',
        title: 'Create a New Campaign',
        body:
          'Select "Create New" to create your first campaign.',
        primaryLabel: 'Next',
        placement: 'bottom',
        pointerPosition: 'left',
      },
      {
        id: 'campaign-form',
        enterRoute: {
          pathname: '/campaigns/new',
          search: CAMPAIGN_NEW_GENERAL_SEARCH,
          state: { campaignType: 'General' },
        },
        targetSelector: '[data-help="create-campaign-form-root"]',
        title: 'Fill Out Your Campaign Details',
        body:
          'Craft a name for your campaign, add your content, and include imagery for a high impact SMS message.',
        primaryLabel: 'Next',
        placement: 'right',
        pointerPosition: 'center',
      },
      {
        id: 'campaign-send',
        enterRoute: {
          pathname: '/campaigns/new',
          search: CAMPAIGN_NEW_GENERAL_SEARCH,
          state: { campaignType: 'General' },
        },
        targetSelector: '[data-help="campaign-form-primary-send"]',
        title: 'Track, Preview, & Send',
        body:
          'Create custom tags to target customers, schedule or send your campaign immediately, and preview your message in real time right to your phone.',
        primaryLabel: 'Create Campaign',
        isFinal: true,
        placement: 'right',
        pointerPosition: 'center',
        scrollIntoView: true,
      },
    ],
  },
};
